import { useCallback, useState } from "react";
import { apiFetch } from "@/lib/api/client";
import type { ReviewShareUser } from "@/types";

interface UseReviewSharesReturn {
  shares: ReviewShareUser[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  fetchShares: (reviewId: string) => Promise<void>;
  saveShares: (reviewId: string, userIds: string[]) => Promise<boolean>;
}

export function useReviewShares(): UseReviewSharesReturn {
  const [shares, setShares] = useState<ReviewShareUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchShares = useCallback(async (reviewId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await apiFetch<ReviewShareUser[]>(`/api/reviews/${reviewId}/shares`);
      setShares(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load shares";
      setError(message);
      setShares([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const saveShares = useCallback(async (reviewId: string, userIds: string[]): Promise<boolean> => {
    setIsSaving(true);
    setError(null);

    try {
      const data = await apiFetch<ReviewShareUser[]>(`/api/reviews/${reviewId}/shares`, {
        method: "PUT",
        body: JSON.stringify({ userIds }),
      });
      setShares(data);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save shares";
      setError(message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, []);

  return {
    shares,
    isLoading,
    isSaving,
    error,
    fetchShares,
    saveShares,
  };
}
